import React, { lazy, Suspense, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Route, Routes, useLocation } from "react-router-dom";
import { AxiosInterceptor } from "./axios_call";
import "./scss/style.scss";
import AuthenticatedRoute from "./route/AuthenticatedRoute";
import UnAuthenticatedRoute from "./route/UnAuthenticatedRoute";
import axios from "axios";
import { BASE_URL } from "./components/constant_api";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AppLoader from "./components/loaders/AppLoader";

// ---- Lazy loaded pages (each becomes its own chunk) ----
const DefaultLayout = lazy(() => import("./hoc/Layout/Layout"));
const Login = lazy(() => import("./components/auth/Login"));
const Logout = lazy(() => import("./components/auth/Logout"));
const OTP = lazy(() => import("./components/auth/OtpScreen"));
const ChangePassword = lazy(() => import("./components/auth/ChangePassword"));
const ResetPassword = lazy(() => import("./components/auth/ResetPassword"));
const ForgotPassword = lazy(() => import("./components/auth/ForgotPassword"));
const VerifyEmail = lazy(() => import("./components/auth/VerifyEmail"));
const RegisterNew = lazy(() => import("./components/auth/RegisterNew"));
const ApprovUser = lazy(() => import("./components/auth/ApprovUser"));
const SetPassword = lazy(() => import("./components/auth/SetPassword"));
const ConfirmOrganisationInvite = lazy(
  () => import("./components/auth/ConfirmInvitation"),
);
const XeroCredentialsGuide = lazy(
  () => import("./containers/Xerocredentialsguide/Xerocredentialsguide"),
);
const SelectBankAccountModal = lazy(
  () => import("./components/Selectbankaccountmodal"),
);

function App() {
  const currentUser = JSON.parse(localStorage.getItem("userDetail"));
  const currentUserId = currentUser?.UserID;
  const location = useLocation();
  const {
    loading: authLoading,
    error: authError,
    isAuthenticated,
  } = useSelector((state) => ({
    loading: state.auth.loading,
    error: state.auth.error,
    isAuthenticated: state.auth?._id !== null,
  }));

  const [showBankAccountModal, setShowBankAccountModal] = useState(false);
  const [organisationId, setOrganisationId] = useState(
    localStorage.getItem("OrganisationID") || "",
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isNew = params.get("isNew");

    if (isNew === "true") {
      handleNewOrganisationRedirect("");
    }
  }, [location.search, currentUserId]);

  useEffect(() => {
    if (organisationId) {
      handleLoggedinUserDetails();
    }
  }, [organisationId]);

  const handleNewOrganisationRedirect = async (organisationIdFromUrl) => {
    try {
      const response = await axios.get(`${BASE_URL}/user/${currentUserId}`);

      const userData = response?.data?.data;

      if (userData && typeof userData === "object") {
        // Store complete user object
        localStorage.setItem("userDetail", JSON.stringify(userData));
        // Store UserID
        if (userData?.UserID) {
          localStorage.setItem(
            "currentLoginUserId",
            JSON.stringify(userData.UserID),
          );
        }

        setShowBankAccountModal(true);
      }
    } catch (error) {
      console.error("Error handling new organisation redirect:", error);
    }
  };

  const handleLoggedinUserDetails = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/user/details/${currentUserId}?OrganisationID=${organisationId}`,
      );

      const userData = response?.data?.data;

      if (userData && typeof userData === "object") {
        // Store complete user object
        localStorage.setItem("userDetail", JSON.stringify(userData));
        // Store UserID
        if (userData?.UserID) {
          localStorage.setItem(
            "currentLoginUserId",
            JSON.stringify(userData.UserID),
          );
        }
      }
    } catch (error) {
      console.error("Error handling loggedin user details:", error);
    }
  };

  const handleBankAccountSubmit = async (
    selectedAccountIds,
    selectedUserIds,
    submitResponse,
  ) => {};

  return (
    <Suspense fallback={<AppLoader />}>
      <AxiosInterceptor>
        <Routes>
          <Route
            exact
            path="/login"
            name="Login Page"
            element={
              <AuthenticatedRoute redirectTo="/query">
                <Login />
              </AuthenticatedRoute>
            }
          />
          <Route
            exact
            path="/forgotpassword"
            name="ForgotPassword Page"
            element={<ForgotPassword />}
          />
          <Route exact path="/otp" name="OTP Page" element={<OTP />} />
          <Route
            exact
            path="/changepassword"
            name="ChangePassword Page"
            element={<ChangePassword />}
          />

          <Route path="/logout" element={<Logout />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/register" element={<RegisterNew />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/approve-user" element={<ApprovUser />} />
          <Route
            path="/confirm-organisation-invite"
            element={<ConfirmOrganisationInvite />}
          />
          <Route path="/set-password" element={<SetPassword />} />
          <Route
            path="/xero-credentials-guide"
            element={<XeroCredentialsGuide />}
          />
          <Route
            path="*"
            name="Home"
            element={
              <UnAuthenticatedRoute redirectTo="/login">
                <DefaultLayout />
              </UnAuthenticatedRoute>
            }
          />
        </Routes>
      </AxiosInterceptor>
      <ToastContainer />
      {/* Only load the modal chunk when it is actually needed */}
      {showBankAccountModal && (
        <SelectBankAccountModal
          isOpen={showBankAccountModal}
          organisationId={organisationId}
          currentUserId={currentUserId}
          onClose={() => setShowBankAccountModal(false)}
          onSubmit={handleBankAccountSubmit}
        />
      )}
    </Suspense>
  );
}

export default App;
